import React, { useCallback, useEffect, useState, useRef } from 'react'
import {
    Folder,
    OrderedUnit,
    Unit,
    UnitVisibility,
} from '../../../../../models'
import { Container } from '../../../../../components'
import { SortableLocationForm } from '../../../../../components/SortableFinder/SortableLocationForm'
import { LocationSelection, Location } from '../../../../../store'
import { SetTemplateUnitsParams } from '../../../../../hooks/useEzSchedule/types'

interface UnitSelectorProps {
    infrastructure: Folder
    finderSelection: LocationSelection
    onUnitSelectionChange: (units: Unit[]) => void
    setTemplateUnits?: (
        params: SetTemplateUnitsParams,
    ) => void | Promise<unknown>
    setOrderedUnits?: React.Dispatch<React.SetStateAction<OrderedUnit[]>>
    flushRef?: React.MutableRefObject<(() => void | Promise<void>) | null>
    phaseId?: number
    initialSelectedUnitCount?: number
    disabledUnitIds?: Set<number>
    disabledUnitReasonById?: Record<number, string>
    linkedUnitIdsByUnitId?: Record<number, number[]>
    defaultOpenFirstLevel?: boolean
    height?: string | number
}

export const UnitSelector: React.FC<UnitSelectorProps> = (
    props: UnitSelectorProps,
) => {
    const {
        infrastructure,
        finderSelection,
        onUnitSelectionChange,
        setTemplateUnits,
        setOrderedUnits,
        flushRef,
        phaseId,
        initialSelectedUnitCount = 0,
        disabledUnitIds,
        disabledUnitReasonById,
        linkedUnitIdsByUnitId,
        defaultOpenFirstLevel,
        height = 'calc(100vh - 230px)',
    } = props

    // Debounce configuration
    const DEBOUNCE_DELAY_MS = 500
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
    const pendingOrderedUnitsRef = useRef<
        { unitId: number; order: number }[] | null
    >(null)
    const hasInitializedRef = useRef(false)
    const previousOrderedUnitCountRef = useRef<number>(0)

    // Flush any pending debounced mutation (awaitable for wizard Continue).
    const flushDebouncedSetTemplateUnits = useCallback(async () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
        }
        if (
            pendingOrderedUnitsRef.current !== null &&
            setTemplateUnits &&
            phaseId !== undefined
        ) {
            const ordered_units = pendingOrderedUnitsRef.current
            pendingOrderedUnitsRef.current = null
            await setTemplateUnits({ ordered_units })
        }
    }, [setTemplateUnits, phaseId])

    // Expose flush function to parent via ref
    useEffect(() => {
        if (flushRef) {
            flushRef.current = flushDebouncedSetTemplateUnits
        }
        return () => {
            if (flushRef) {
                flushRef.current = null
            }
        }
    }, [flushRef, flushDebouncedSetTemplateUnits])

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    // Reset per-phase initialization behavior to avoid cross-phase carryover.
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
        }
        pendingOrderedUnitsRef.current = null
        previousOrderedUnitCountRef.current = initialSelectedUnitCount
        hasInitializedRef.current = initialSelectedUnitCount === 0
    }, [phaseId, initialSelectedUnitCount])

    // Debounced call to setTemplateUnits
    const debouncedSetTemplateUnits = useCallback(
        (units: { unitId: number; order: number }[]) => {
            if (!setTemplateUnits || phaseId === undefined) return

            // Store the pending value
            pendingOrderedUnitsRef.current = units

            // Clear any existing timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }

            // Set a new timer
            debounceTimerRef.current = setTimeout(() => {
                if (pendingOrderedUnitsRef.current !== null) {
                    setTemplateUnits({
                        ordered_units: pendingOrderedUnitsRef.current,
                    })
                    pendingOrderedUnitsRef.current = null
                }
                debounceTimerRef.current = null
            }, DEBOUNCE_DELAY_MS)
        },
        [setTemplateUnits, phaseId],
    )

    // Track UI sort state to mirror the SortableFinder behavior
    const [uiSortState, setUiSortState] = useState<{
        [folderId: number]: 'asc' | 'desc'
    }>({})
    const [uiUnitOrderState, setUiUnitOrderState] = useState<{
        [folderId: number]: number[]
    }>({})

    // Function to get all units in the same order as the UI displays them
    const getAllUnitsInUIOrder = useCallback(
        (root: Folder): Unit[] => {
            const allUnits: Unit[] = []

            const traverseFolder = (folder: Folder) => {
                // Get units for this folder in UI order
                let folderUnits = [...folder.units]

                // Apply UI sorting logic (same as SortableFinder)
                if (uiUnitOrderState[folder.id]) {
                    // Use custom order if available
                    const orderedIds = uiUnitOrderState[folder.id]
                    folderUnits = orderedIds
                        .map((id) => folderUnits.find((unit) => unit.id === id))
                        .filter((unit): unit is Unit => unit !== undefined)
                } else {
                    // Use default sorting
                    const sortOrder = uiSortState[folder.id] || 'asc'
                    folderUnits.sort((a, b) => {
                        return sortOrder === 'asc'
                            ? a.name.localeCompare(b.name, undefined, {
                                  numeric: true,
                                  sensitivity: 'base',
                              })
                            : b.name.localeCompare(a.name, undefined, {
                                  numeric: true,
                                  sensitivity: 'base',
                              })
                    })
                }

                // Add units to the result
                allUnits.push(...folderUnits)

                // Sort children folders before processing them (this is the key fix!)
                const sortedChildren = [...folder.children].sort((a, b) => {
                    const sortOrder = uiSortState[folder.id] || 'asc'
                    return sortOrder === 'asc'
                        ? a.name.localeCompare(b.name, undefined, {
                              numeric: true,
                              sensitivity: 'base',
                          })
                        : b.name.localeCompare(a.name, undefined, {
                              numeric: true,
                              sensitivity: 'base',
                          })
                })

                // Recursively process children in sorted order
                sortedChildren.forEach((child) => traverseFolder(child))
            }

            traverseFolder(root)
            return allUnits
        },
        [uiSortState, uiUnitOrderState],
    )

    // Function to generate ordered units from current selection and UI order
    const generateOrderedUnits = useCallback(() => {
        // Get all selected units and filter out any invalid units
        const selectedUnits = Object.values(finderSelection.unit).filter(
            (u): u is Unit =>
                u !== undefined &&
                typeof u === 'object' &&
                'id' in u &&
                u.id !== undefined,
        )

        // Get all units in UI order
        const allUnitsInOrder = getAllUnitsInUIOrder(infrastructure)

        // Filter to only include selected units and maintain UI order
        const selectedUnitIds = selectedUnits.map((u) => u.id)
        const orderedSelectedUnits = allUnitsInOrder.filter((unit) =>
            selectedUnitIds.includes(unit.id),
        )

        const orderedUnits: OrderedUnit[] = orderedSelectedUnits.map(
            (unit, index) => ({
                unitId: unit.id,
                unitName: unit.name,
                folderName: unit.folder.name,
                order: index + 1000,
            }),
        )

        setOrderedUnits?.(orderedUnits)

        const previousCount = previousOrderedUnitCountRef.current
        previousOrderedUnitCountRef.current = orderedUnits.length

        // Call debounced mutation (skip initial load to avoid unnecessary API call)
        if (hasInitializedRef.current) {
            // Avoid emitting repeated empty payloads from initialization churn.
            if (orderedUnits.length === 0 && previousCount === 0) {
                return
            }
            // Map to the format expected by the API
            const apiOrderedUnits = orderedUnits.map(({ unitId, order }) => ({
                unitId,
                order,
            }))
            debouncedSetTemplateUnits(apiOrderedUnits)
        } else if (orderedUnits.length > 0) {
            // Mark as initialized after first population from server data
            hasInitializedRef.current = true
        }
    }, [
        finderSelection,
        infrastructure,
        getAllUnitsInUIOrder,
        debouncedSetTemplateUnits,
    ])

    // Generate ordered units whenever finderSelection changes (including initial load)
    useEffect(() => {
        generateOrderedUnits()
    }, [generateOrderedUnits])

    // Generate ordered units on initial infrastructure load
    useEffect(() => {
        if (infrastructure) {
            generateOrderedUnits()
        }
    }, [infrastructure, generateOrderedUnits])

    const collectUnitsFromFolder = useCallback((folder: Folder): Unit[] => {
        let units = [...folder.units]
        folder.children.forEach((child) => {
            units = units.concat(collectUnitsFromFolder(child))
        })
        return units
    }, [])

    const handleLocationSelection = useCallback(
        (location: Location) => {
            // Get current selected units
            const currentSelectedUnits = Object.values(
                finderSelection.unit,
            ).filter((u) => u !== undefined) as Unit[]
            const isSelectionDisabled = (unitId: number): boolean => {
                return (
                    disabledUnitIds?.has(unitId) === true &&
                    finderSelection.unit[unitId] === undefined
                )
            }
            const unitById = new Map(
                collectUnitsFromFolder(infrastructure).map((unit) => [
                    unit.id,
                    unit,
                ]),
            )
            const expandUnitIds = (
                unitIds: number[],
                adding: boolean,
            ): number[] => {
                const expanded = new Set(unitIds)
                unitIds.forEach((unitId) => {
                    const linked = linkedUnitIdsByUnitId?.[unitId] ?? []
                    linked.forEach((linkedId) => {
                        if (adding && isSelectionDisabled(linkedId)) {
                            return
                        }
                        expanded.add(linkedId)
                    })
                })
                return Array.from(expanded)
            }

            if (location.type === 'unit') {
                const unit = location.location as Unit

                if (finderSelection.unit[unit.id] !== undefined) {
                    const removeIds = new Set(expandUnitIds([unit.id], false))
                    const filteredUnits = currentSelectedUnits.filter(
                        (u) => !removeIds.has(u.id),
                    )
                    onUnitSelectionChange(filteredUnits)
                } else {
                    if (isSelectionDisabled(unit.id)) {
                        return
                    }
                    const addIds = expandUnitIds([unit.id], true)
                    const unitsToAdd = addIds
                        .map((unitId) => unitById.get(unitId))
                        .filter((next): next is Unit => next !== undefined)
                        .filter(
                            (next) =>
                                finderSelection.unit[next.id] === undefined,
                        )
                    onUnitSelectionChange([
                        ...currentSelectedUnits,
                        ...unitsToAdd,
                    ])
                }
            } else if (location.type === 'folder') {
                const folder = location.location as Folder

                // Get all units in this folder and its children recursively
                const getAllUnits = (folder: Folder): Unit[] => {
                    let units = [...folder.units]
                    folder.children.forEach((child) => {
                        units = units.concat(getAllUnits(child))
                    })
                    return units
                }

                const allUnits = getAllUnits(folder)
                const selectableUnits = allUnits.filter(
                    (unit) => !isSelectionDisabled(unit.id),
                )

                if (selectableUnits.length === 0) {
                    return
                }

                // Check if folder is currently selected (any units are selected)
                const hasSelectedUnits = selectableUnits.some(
                    (unit) => finderSelection.unit[unit.id] !== undefined,
                )

                if (hasSelectedUnits) {
                    const removeIds = new Set(
                        expandUnitIds(
                            selectableUnits.map((unit) => unit.id),
                            false,
                        ),
                    )
                    const filteredUnits = currentSelectedUnits.filter(
                        (unit) => !removeIds.has(unit.id),
                    )
                    onUnitSelectionChange(filteredUnits)
                } else {
                    const addIds = expandUnitIds(
                        selectableUnits.map((unit) => unit.id),
                        true,
                    )
                    const unitsToAdd = addIds
                        .map((unitId) => unitById.get(unitId))
                        .filter((next): next is Unit => next !== undefined)
                        .filter(
                            (next) =>
                                finderSelection.unit[next.id] === undefined &&
                                !isSelectionDisabled(next.id),
                        )
                    onUnitSelectionChange([
                        ...currentSelectedUnits,
                        ...unitsToAdd,
                    ])
                }
            }
        },
        [
            finderSelection,
            onUnitSelectionChange,
            generateOrderedUnits,
            disabledUnitIds,
            infrastructure,
            collectUnitsFromFolder,
            linkedUnitIdsByUnitId,
        ],
    )

    const handleUnitOrderChange = useCallback(
        (folderId: number, unitIds: number[]) => {
            // Update UI unit order state to mirror SortableFinder
            setUiUnitOrderState((prev) => ({
                ...prev,
                [folderId]: unitIds,
            }))
        },
        [],
    )

    const handleSortChange = useCallback(
        (folderId: number, order: 'asc' | 'desc') => {
            // Update UI sort state to mirror SortableFinder
            setUiSortState((prev) => ({
                ...prev,
                [folderId]: order,
            }))

            // Clear any custom unit order for this folder when sort changes
            setUiUnitOrderState((prev) => {
                const newState = { ...prev }
                delete newState[folderId]
                return newState
            })
        },
        [],
    )
    const disabledUnitReasonProps = {
        disabledUnitReasonById,
    } as const

    return (
        <Container style={{ height }}>
            <SortableLocationForm
                root={infrastructure}
                locationSelection={finderSelection}
                setLocationSelection={handleLocationSelection}
                useCheckbox
                useCase="schedule"
                enableSorting
                defaultSortOrder="asc"
                onSortChange={handleSortChange}
                onUnitOrderChange={handleUnitOrderChange}
                maxHeight="100%"
                disabledUnitIds={disabledUnitIds}
                defaultOpenFirstLevel={defaultOpenFirstLevel}
                {...(disabledUnitReasonProps as any)}
            />
        </Container>
    )
}
